import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { VerifiedUser, HelpOutline } from "@mui/icons-material";
import InitiatorTrustChecklist from "../Contracts/ContractView/InitiatorTrustChecklist";
import type { InitiatorTrustPayload } from "../Contracts/ContractView/InitiatorTrustBanner";
import { getInitiatorCriteria, getInitiatorTrustFixActions, needsInitiatorSelfieVerification, type InitiatorTrustSettings } from "../Contracts/ContractView/initiatorTrustCriteria";
import InitiatorTrustProfilePopup from "./InitiatorTrustProfilePopup";
import InitiatorTrustSelfieVerifyPopup from "./InitiatorTrustSelfieVerifyPopup";
import { checkPermission, UserPermission } from "../../methods/permissionByRole";
import { loadSelf } from "../../methods/loadSelf";
import { refreshInitiatorTrust } from "../../methods/refreshInitiatorTrust";
import notifyUser, { notifyAboutError } from "../../methods/notifyUser";
import "pages/Styles/UIKit_v2.sass";
import "pages/Styles/Cells.sass";

type Props = {
    $store: any;
    $commitToStore: (data: any) => void;
    onRequestEditProfile: () => void;
};

type BannerVisualState = "success" | "warning" | "pending";

function bannerVisualState(
    trust: InitiatorTrustPayload | null | undefined,
    selfieDisplayMode?: string,
    trustSettings?: Partial<InitiatorTrustSettings> | null,
): BannerVisualState {
    if (!trust || trust.status === "pending") {
        return "pending";
    }
    if (trust.status !== "ready") {
        return "warning";
    }
    if (trust.customer?.selfie_check === "PENDING") {
        return "pending";
    }
    const criteria = getInitiatorCriteria(trust, {
        selfieDisplayMode,
        viewerIsOwner: true,
        trustSettings,
    });
    if (criteria.length > 0 && criteria.every((c) => c.ok)) {
        return "success";
    }
    return "warning";
}

function bannerTitle(state: BannerVisualState, selfiePending?: boolean): string {
    if (selfiePending) {
        return "Проверка селфи выполняется";
    }
    if (state === "success") {
        return "Подписанты видят: инициатор проверен";
    }
    if (state === "pending") {
        return "Проверка профиля выполняется";
    }
    return "Подписанты видят предупреждение";
}

function bannerModifier(state: BannerVisualState): string {
    if (state === "success") {
        return "initiator-trust-banner-v2--success";
    }
    if (state === "pending") {
        return "initiator-trust-banner-v2--warning";
    }
    return "initiator-trust-banner-v2--warning";
}

function shieldColor(state: BannerVisualState): string {
    if (state === "success") {
        return "#4CAF50";
    }
    return "#F9A825";
}

function ProfileInitiatorTrustSection({ $store, $commitToStore, onRequestEditProfile }: Props) {
    const [popupOpen, setPopupOpen] = useState(false);
    const [selfiePopupOpen, setSelfiePopupOpen] = useState(false);
    const [registryRetryLoading, setRegistryRetryLoading] = useState(false);
    const user = $store.userSession;
    const displayMode = user?.initiator_trust_display_mode;
    const selfieDisplayMode = user?.initiator_trust_selfie_display_mode;
    const trustSettings = user?.initiator_trust_settings;
    const trust: InitiatorTrustPayload | null | undefined = user?.self_initiator_trust;

    useEffect(() => {
        if (!$store.openInitiatorTrustProfilePopup) {
            return;
        }
        if ($store.openInitiatorTrustSelfiePopup) {
            setSelfiePopupOpen(true);
        } else {
            setPopupOpen(true);
        }
        $commitToStore({
            openInitiatorTrustProfilePopup: false,
            openInitiatorTrustSelfiePopup: false,
        });
    }, [$store.openInitiatorTrustProfilePopup, $store.openInitiatorTrustSelfiePopup, $commitToStore]);

    useEffect(() => {
        if (!$store.scrollToInitiatorTrustSection) {
            return;
        }
        $commitToStore({ scrollToInitiatorTrustSection: false });
        const tryScroll = (attempt: number) => {
            const el = document.getElementById("profile-initiator-trust-section");
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            if (attempt < 8) {
                setTimeout(() => tryScroll(attempt + 1), 120);
            }
        };
        setTimeout(() => tryScroll(0), 150);
    }, [$store.scrollToInitiatorTrustSection, $commitToStore]);

    const selfiePending = trust?.customer?.selfie_check === "PENDING";
    useEffect(() => {
        if (!selfiePending) {
            return;
        }
        const tick = () => {
            loadSelf({ $store, $commitToStore });
        };
        const id = window.setInterval(tick, 15000);
        return () => window.clearInterval(id);
    }, [selfiePending, $store, $commitToStore]);

    if (displayMode === "hidden" && selfieDisplayMode === "hidden") {
        return null;
    }

    if (user?.initiator_trust_profile_section_visible === false) {
        return null;
    }
    if (
        user?.initiator_trust_profile_section_visible === undefined
        && !trust
        && (user?.contracts_count ?? 0) <= 0
    ) {
        return null;
    }

    const isLoggedInViaRole = user?.role_id != null;
    if (!checkPermission({ $store }, UserPermission.OWNER) || isLoggedInViaRole) {
        return null;
    }

    const visual = bannerVisualState(trust, selfieDisplayMode, trustSettings);
    const mod = bannerModifier(visual);
    const popupTrust: InitiatorTrustPayload = trust ?? { status: "pending" };
    const quickActions = getInitiatorTrustFixActions(popupTrust);
    const primaryAction = quickActions[0] ?? (!trust || trust.status === "pending"
        ? { id: "refresh" as const, label: "Обновить статус" }
        : undefined);
    const showSelfieVerify = needsInitiatorSelfieVerification(trust, selfieDisplayMode);
    const selfieButtonLabel = trust?.customer?.selfie_check === "FAILED" || trust?.customer?.selfie_check === "ERROR"
        ? "Повторить проверку селфи"
        : "Проверить селфи с паспортом";

    async function handleAction(action: { id: string }) {
        if (action.id === "edit_profile") {
            setPopupOpen(false);
            onRequestEditProfile();
            return;
        }
        if (action.id === "refresh") {
            await loadSelf({ $store, $commitToStore });
            return;
        }
        if (action.id === "retry_registry_check") {
            setRegistryRetryLoading(true);
            try {
                const result = await refreshInitiatorTrust(true);
                if (result.self_initiator_trust) {
                    $commitToStore({
                        userSession: {
                            ...user,
                            self_initiator_trust: result.self_initiator_trust,
                        },
                    });
                }
                if (result.support_manual_verification_requested) {
                    notifyUser(
                        { $store, $commitToStore },
                        "Проверка не пройдена",
                        "Не нашли данных по вашим реквизитам. Проверьте ИНН, ОГРН и юридическое название — или напишите в техподдержку, если считаете, что это ошибка.",
                    );
                } else {
                    notifyUser(
                        { $store, $commitToStore },
                        "Проверка обновлена",
                        "Данные по организации пересчитаны.",
                    );
                }
            } catch (err) {
                notifyAboutError({ $store, $commitToStore }, err);
            } finally {
                setRegistryRetryLoading(false);
            }
        }
    }

    return (
        <div
            id="profile-initiator-trust-section"
            className="profile-section-card ui-kit-v2"
            style={{ marginTop: 15, marginBottom: 15 }}
        >
            <div className="profile-section-title">Проверка для подписантов</div>
            <div className="profile-section-subtitle" style={{ marginBottom: 12 }}>
                Так подписант видит вашу надёжность при открытии договора. Исправьте данные профиля,
                если какой-то критерий не пройден.
            </div>

            <div
                className={`cell-card disabledHover initiator-trust-banner-v2 ${mod}`}
                role="region"
                aria-label="Проверка инициатора для подписантов"
            >
                <button
                    type="button"
                    className="cell-card-content"
                    onClick={() => setPopupOpen(true)}
                    style={{
                        width: "100%",
                        textAlign: "left",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        font: "inherit",
                    }}
                >
                    <div className="initiator-trust-banner-v2__header">
                        <VerifiedUser sx={{ fontSize: 24, color: shieldColor(visual), flexShrink: 0 }} />
                        <p className="initiator-trust-banner-v2__title">
                            {bannerTitle(visual, selfiePending)}
                        </p>
                        <HelpOutline className="initiator-trust-banner-v2__help" sx={{ fontSize: 20 }} />
                    </div>
                    <div className="initiator-trust-banner-v2__body">
                        {trust && trust.status === "ready" ? (
                            <InitiatorTrustChecklist
                                trust={trust}
                                iconSize={18}
                                compact
                                selfieDisplayMode={selfieDisplayMode}
                                viewerIsOwner
                                trustSettings={trustSettings}
                            />
                        ) : (
                            <p className="initiator-trust-banner-v2__line">
                                {trust?.status === "pending"
                                    ? "Обновите страницу через минуту или нажмите кнопку ниже."
                                    : "Данные проверки ещё не готовы."}
                            </p>
                        )}
                    </div>
                </button>
            </div>

            {primaryAction && (
                <div className="profile-section-row" style={{ marginTop: 12 }}>
                    <button
                        type="button"
                        className="new ui-kit-v2-button action-button primary"
                        onClick={() => handleAction(primaryAction)}
                        disabled={registryRetryLoading && primaryAction.id === "retry_registry_check"}
                    >
                        <span className="button-text">
                            {registryRetryLoading && primaryAction.id === "retry_registry_check"
                                ? "Проверяем…"
                                : primaryAction.label}
                        </span>
                    </button>
                </div>
            )}

            {showSelfieVerify && (
                <div className="profile-section-row" style={{ marginTop: primaryAction ? 10 : 12 }}>
                    <button
                        type="button"
                        className={`new ui-kit-v2-button action-button ${primaryAction ? "secondary" : "primary"}`}
                        onClick={() => setSelfiePopupOpen(true)}
                    >
                        <span className="button-text">{selfieButtonLabel}</span>
                    </button>
                </div>
            )}

            {selfiePopupOpen && (
                <InitiatorTrustSelfieVerifyPopup
                    onClose={() => setSelfiePopupOpen(false)}
                />
            )}

            {popupOpen && (
                <InitiatorTrustProfilePopup
                    trust={popupTrust}
                    onClose={() => setPopupOpen(false)}
                    onAction={handleAction}
                    selfieDisplayMode={selfieDisplayMode}
                    trustSettings={trustSettings}
                    onOpenSelfieVerify={() => {
                        setPopupOpen(false);
                        setSelfiePopupOpen(true);
                    }}
                    showSelfieVerify={showSelfieVerify}
                    selfieButtonLabel={selfieButtonLabel}
                />
            )}
        </div>
    );
}

export default connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: "S" }),
    }),
)(ProfileInitiatorTrustSection);
